import { ColumnDef } from '@tanstack/react-table'
import { Box, Flex, Loading, Table, Text } from '@workwhile/ui'
import { ShiftWorkBreak } from 'api/shifts/getShiftWorkBreaks'
import { TimeConversionPopover } from 'components/Shared/TimeConversion'
import { formatInTimeZone } from 'date-fns-tz'
import { toTitleCase } from 'lib/string'
import { getLocalTimezone } from 'lib/time'
import { Pencil } from 'lucide-react'
import { useShiftWorkBreaksQuery } from 'queries/shifts'
import React, { useMemo, useState } from 'react'
import { ActionIcon } from './ActionIcon/ActionIcon'
import { EditBreakModal } from './EditBreak/EditBreakModal'

const TIME_FORMAT = 'h:mm a z'

const BREAK_ISSUE_LABEL: Record<
  NonNullable<ShiftWorkBreak['issue']>,
  string
> = {
  required_not_recorded:
    'Worker did not record taking this required break. Update break with details on when the worker took the break.',
  in_progress:
    'Break is still in progress. Update break with details on when it ended.',
  required_not_taken:
    'Worker claimed they have not taken this required break. Update break with details on when the worker took the break.',
}

const getBreakIssueLabel = (issue: string): string =>
  BREAK_ISSUE_LABEL[issue as keyof typeof BREAK_ISSUE_LABEL] ??
  'Unsupported Enum Value'

const BreakTime = ({
  value,
  timezone,
}: {
  value: string | null | undefined
  timezone: string
}) => {
  if (!value) {
    return <>-</>
  }

  return (
    <TimeConversionPopover
      value={value}
      zones={[
        { timeZone: getLocalTimezone(), context: 'Your computer' },
        { timeZone: timezone, context: 'Shift' },
        { timeZone: 'UTC' },
      ]}
      primaryTimeZone={timezone}
      side="top"
    >
      {formatInTimeZone(value, timezone, TIME_FORMAT)}
    </TimeConversionPopover>
  )
}

const getColumns = (
  timezone: string,
  onEdit: ((shiftWorkBreak: ShiftWorkBreak) => void) | undefined
): ColumnDef<ShiftWorkBreak>[] => [
  {
    header: 'Break #',
    accessorKey: 'breakNumber',
  },
  {
    header: 'Type',
    accessorKey: 'breakType',
    cell: ({ row }) => toTitleCase(row.original.breakType),
  },
  {
    header: 'Requirement',
    accessorKey: 'requirementType',
    cell: ({ row }) => toTitleCase(row.original.requirementType),
  },
  {
    header: 'Duration',
    accessorKey: 'durationInMinutes',
    cell: ({ row }) => `${row.original.durationInMinutes} min`,
  },
  {
    header: 'Start',
    accessorKey: 'startTime',
    cell: ({ row }) => (
      <BreakTime value={row.original.startTime} timezone={timezone} />
    ),
  },
  {
    header: 'End',
    accessorKey: 'endTime',
    cell: ({ row }) => (
      <BreakTime value={row.original.endTime} timezone={timezone} />
    ),
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) =>
      row.original.status ? toTitleCase(row.original.status) : 'Not recorded',
  },
  {
    header: 'Reason',
    accessorKey: 'reason',
    cell: ({ row }) => row.original.reason || '-',
  },
  {
    header: 'Pay Treatment',
    accessorKey: 'payTreatment',
    cell: ({ row }) => toTitleCase(row.original.payTreatment),
  },
  {
    header: 'Compliance',
    accessorKey: 'issue',
    cell: ({ row }) =>
      row.original.issue ? (
        <Text color="red" style={{ whiteSpace: 'normal' }}>
          {getBreakIssueLabel(row.original.issue)}
        </Text>
      ) : (
        <Text color="green">Compliant</Text>
      ),
  },
  ...(onEdit
    ? [
        {
          header: '',
          id: 'edit',
          cell: ({ row }: { row: { original: ShiftWorkBreak } }) => (
            <ActionIcon
              icon={Pencil}
              title="Edit Break"
              onClick={() => onEdit(row.original)}
            />
          ),
        },
      ]
    : []),
]

type WorkerRowBreaksTableProps = {
  shiftId: number
  workId: number
  timezone: string
  clockedInAt?: string | null
  clockedOutAt?: string | null
  allowEdit?: boolean
  onEditClose?: () => void
}

export const WorkerRowBreaksTable = ({
  shiftId,
  workId,
  timezone,
  clockedInAt,
  clockedOutAt,
  allowEdit,
  onEditClose,
}: WorkerRowBreaksTableProps) => {
  const { data, isFetching } = useShiftWorkBreaksQuery({ shiftId, workId })
  const [editingBreak, setEditingBreak] = useState<ShiftWorkBreak>()
  const columns = useMemo(
    () => getColumns(timezone, allowEdit ? setEditingBreak : undefined),
    [timezone, allowEdit]
  )

  return (
    <Box>
      {data && data.length > 0 ? (
        <Table columns={columns} data={data} textVerticalAlign="top" />
      ) : (
        <Flex
          mb={2}
          flexDirection="column"
          alignItems="center"
          color="lightText"
          height={100}
          justifyContent="center"
          maxWidth={['100%', '100%', '50%', '50%']}
          margin="auto"
        >
          {isFetching ? <Loading /> : <Text>No breaks found.</Text>}
        </Flex>
      )}
      {editingBreak ? (
        <EditBreakModal
          shiftId={shiftId}
          workId={workId}
          shiftWorkBreak={editingBreak}
          timezone={timezone}
          clockedInAt={clockedInAt}
          clockedOutAt={clockedOutAt}
          onClose={() => {
            setEditingBreak(undefined)
            onEditClose?.()
          }}
        />
      ) : null}
    </Box>
  )
}
